"use client";
import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { Camera } from "lucide-react";
import RotatingText from "../../../common/sections/RotationText";
import StarRating from "../../StarRating";
import { RotatingTextItem } from "@/app/components/common/sections/RotationText";
import { Product, ProductVariantSize } from "@/app/lib/features/product/product.types";
import FallbackImage from "@/app/components/elements/FallbackImage";
import { cn } from "@/app/utils/cn";

interface ProductCardInfoProps {
  brandName?: string;
  productName: string;
  productDescription: string;
  ratingAverage: number;
  ratingCount: number;
  socialProofItems: RotatingTextItem[];
  campaignItems: RotatingTextItem[];
  isOutfit?: boolean;
  outfitProduct?: Product;
  onOutfitVariantChange?: (variantId: number) => void;
  onOutfitColorImageChange?: (image: string) => void;
}

const ProductCardInfo = ({
  brandName,
  productName,
  productDescription,
  ratingAverage,
  ratingCount,
  socialProofItems,
  campaignItems,
  isOutfit,
  outfitProduct,
  onOutfitVariantChange,
  onOutfitColorImageChange,
}: ProductCardInfoProps) => {
  const colors = useMemo(
    () => outfitProduct?.variants_by_color ?? [],
    [outfitProduct],
  );

  const [selectedColorIdx, setSelectedColorIdx] = useState(0);

  const sizes = useMemo<ProductVariantSize[]>(
    () => colors[selectedColorIdx]?.sizes ?? [],
    [colors, selectedColorIdx],
  );

  const defaultSizeIdx = useMemo(
    () => Math.max(sizes.findIndex((s) => s.stock > 0), 0),
    [sizes],
  );
  const [selectedSizeIdx, setSelectedSizeIdx] = useState(defaultSizeIdx);

  // Stable refs to avoid stale closure issues in callbacks
  const onOutfitVariantChangeRef = useRef(onOutfitVariantChange);
  const onOutfitColorImageChangeRef = useRef(onOutfitColorImageChange);

  useEffect(() => {
    onOutfitVariantChangeRef.current = onOutfitVariantChange;
  }, [onOutfitVariantChange]);

  useEffect(() => {
    onOutfitColorImageChangeRef.current = onOutfitColorImageChange;
  }, [onOutfitColorImageChange]);

  const handleColorSelect = useCallback(
    (e: React.MouseEvent, idx: number) => {
      e.preventDefault();
      e.stopPropagation();
      setSelectedColorIdx(idx);
      const newSizes: ProductVariantSize[] = colors[idx]?.sizes ?? [];
      const newDefault = Math.max(newSizes.findIndex((s) => s.stock > 0), 0);
      setSelectedSizeIdx(newDefault);
      const variantId =
        newSizes[newDefault]?.variant_id ??
        colors[idx]?.variant_id ??
        outfitProduct?.variant_id ??
        0;
      onOutfitVariantChangeRef.current?.(variantId);
      const image = colors[idx]?.primary_image;
      if (image) onOutfitColorImageChangeRef.current?.(image);
    },
    [colors, outfitProduct],
  );

  const handleSizeSelect = useCallback(
    (e: React.MouseEvent, idx: number) => {
      e.preventDefault();
      e.stopPropagation();
      setSelectedSizeIdx(idx);
      const variantId =
        sizes[idx]?.variant_id ??
        colors[selectedColorIdx]?.variant_id ??
        outfitProduct?.variant_id ??
        0;
      onOutfitVariantChangeRef.current?.(variantId);
    },
    [sizes, colors, selectedColorIdx, outfitProduct],
  );

  const hasColors = colors.length > 1;
  const hasSizes = sizes.length > 0;

  return (
    <div className="space-y-0.5 sm:space-y-1.5 mb-1 flex-1 flex flex-col">
      <h2 className="text-[10px] sm:text-sm text-gray-900 line-clamp-1 leading-3 sm:leading-4 group-hover/card:text-main transition-colors duration-300">
        {brandName && <span className="font-bold">{brandName} </span>}
        <span className="font-semibold">{productName}</span>
      </h2>

      {/* Outfit: renk görselleri + bedenler — ismin hemen altında */}
      {isOutfit && outfitProduct && (hasColors || hasSizes) && (
        <div className="space-y-1.5 min-h-15" onClick={(e) => e.preventDefault()}>
          {/* Colors */}
          {hasColors && (
            <div className="flex flex-wrap gap-1">
              {colors.map((color, idx) => (
                <button
                  key={color.variant_id}
                  type="button"
                  title={color.color_name}
                  onClick={(e) => handleColorSelect(e, idx)}
                  className={cn(
                    "relative w-8 h-10 sm:w-9 sm:h-12 rounded-md border-2 overflow-hidden transition-all shrink-0",
                    selectedColorIdx === idx
                      ? "border-main shadow-md scale-105"
                      : "border-gray-200 hover:border-gray-400",
                  )}
                >
                  <FallbackImage
                    src={color.primary_image}
                    alt={color.color_name}
                    fill
                    className="object-cover"
                    sizes="36px"
                  />
                </button>
              ))}
            </div>
          )}

          {/* Sizes */}
          {hasSizes && (
            <div className="flex flex-wrap gap-1">
              {sizes.map((size, idx) => {
                const isSelected = idx === selectedSizeIdx;
                const isOutOfStock = size.stock === 0;
                return (
                  <button
                    key={size.id}
                    type="button"
                    onClick={(e) => !isOutOfStock && handleSizeSelect(e, idx)}
                    disabled={isOutOfStock}
                    className={cn(
                      "h-7 px-1.5 min-w-7 sm:h-8 sm:min-w-8 border-2 rounded-lg font-medium text-[10px] sm:text-xs transition-all duration-200 flex items-center justify-center",
                      isOutOfStock
                        ? "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed line-through"
                        : isSelected
                          ? "border-main bg-main text-white shadow-md"
                          : "border-gray-300 bg-white text-gray-700 hover:border-main hover:text-main hover:shadow-sm",
                    )}
                  >
                    {size.size}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* outfit'te de diğer bilgiler gösterilir */}
      <p className="text-[9px] sm:text-xs text-gray-600 line-clamp-1 leading-3">
        {productDescription}
      </p>
      {socialProofItems.length > 0 && (
        <RotatingText
          items={socialProofItems}
          mainClassName="text-[9px] sm:text-xs"
          rotationInterval={3000}
          className="justify-start py-0"
        />
      )}
      <div className="flex items-center gap-1 sm:gap-3">
        <div className="flex items-center sm:gap-3">
          <p className="text-[9px] sm:text-[11px] text-muted-foreground">
            ({ratingAverage.toFixed(1)})
          </p>
          <StarRating rating={ratingAverage} size="xs" className="sm:scale-125" />
        </div>
        <div className="flex items-center gap-0.5 sm:gap-1">
          <p className="text-[9px] sm:text-[11px] text-muted-foreground">
            ({ratingCount})
          </p>
          <Camera className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-muted-foreground" />
        </div>
      </div>
      {campaignItems.length > 0 && (
        <RotatingText
          items={campaignItems}
          mainClassName="text-[9px] sm:text-xs"
          rotationInterval={2500}
          className="justify-start py-0"
        />
      )}
    </div>
  );
};

export default ProductCardInfo;